
import { useState, useRef } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Profile } from "@/hooks/profile/types";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/utils/imageCompression";
import { useNotifications } from "@/hooks/useNotifications";

interface AvatarUploaderProps {
  profile: Profile;
  onAvatarChange: (avatarUrl: string) => Promise<void>;
}

export const AvatarUploader = ({ profile, onAvatarChange }: AvatarUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error: showError } = useNotifications();

  const getInitials = () => {
    const first = profile.first_name?.charAt(0) || "";
    const last = profile.last_name?.charAt(0) || "";
    if (first || last) return `${first}${last}`.toUpperCase();
    return profile.username?.charAt(0)?.toUpperCase() || "U";
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showError("Por favor selecciona un archivo de imagen");
      return;
    }

    // Validate file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      showError("La imagen no debe superar 5MB");
      return;
    }

    setIsUploading(true);

    try {
      // Compress the image
      const compressedBlob = await compressImage(file, 200, 200, 0.7);
      
      // Generate unique filename
      const timestamp = Date.now();
      const filePath = `avatars/${profile.id}/${timestamp}.jpg`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(filePath, compressedBlob, {
          contentType: "image/jpeg",
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("images")
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      await onAvatarChange(urlData.publicUrl);
      
      success("Avatar actualizado correctamente");
    } catch (err) {
      console.error("Error uploading avatar:", err);
      showError("Error al subir el avatar. Intenta nuevamente.");
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 py-6">
      {/* Avatar with decorative ring */}
      <div 
        className="relative cursor-pointer group"
        onClick={handleClick}
      >
        {/* Outer decorative ring */}
        <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-primary/40 via-primary/20 to-primary/40 opacity-75" />
        
        {/* Inner ring */}
        <div className="absolute -inset-0.5 rounded-full bg-background" />
        
        <Avatar className="relative h-28 w-28 border-2 border-primary/20 shadow-lg">
          <AvatarImage 
            src={profile.avatar_url || undefined} 
            alt="Avatar" 
            className="object-cover"
          />
          <AvatarFallback className="text-3xl font-semibold bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
            {getInitials()}
          </AvatarFallback>
        </Avatar>
        
        {/* Hover overlay */}
        <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {isUploading ? (
            <Loader2 className="h-7 w-7 text-white animate-spin" />
          ) : (
            <Camera className="h-7 w-7 text-white" />
          )}
        </div>

        {/* Loading overlay when uploading */}
        {isUploading && (
          <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
            <Loader2 className="h-7 w-7 text-white animate-spin" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <p className="text-sm text-muted-foreground/80 hover:text-muted-foreground transition-colors">
        Toca para cambiar tu foto
      </p>
    </div>
  );
};
